/**
 * Notification Center Component
 * Displays and manages user notifications
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import UserAvatar from './common/UserAvatar';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon,
  XMarkIcon,
  DocumentTextIcon,
  ChatBubbleLeftIcon,
  ArrowUturnLeftIcon,
  AtSymbolIcon,
  UserPlusIcon,
  FlagIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const NotificationCenter = ({ isOpen, onClose, user }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/platform/notifications', {
        params: { page: 1, page_size: 50 }
      });
      setNotifications(response.data.data?.notifications || []);
      setUnreadCount(response.data.data?.unread_count || 0);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get('/api/platform/notifications/unread-count');
      setUnreadCount(response.data.data?.unread_count || 0);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`/api/platform/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put('/api/platform/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notification_id);
    
    // Navigate based on notification type
    if (notification.post_id) {
      // Navigate to post (would need a post detail route)
      navigate(`/feed?post=${notification.post_id}`);
    } else if (notification.entity_type && notification.entity_id) {
      // Navigate based on entity type
      if (notification.entity_type === 'vision') {
        navigate(`/visions/${notification.entity_id}`);
      }
    }
    
    onClose();
  };

  const getNotificationIcon = (type) => {
    const iconClass = 'w-5 h-5 text-secondary shrink-0';
    switch (type) {
      case 'new_post':
        return <DocumentTextIcon className={iconClass} />;
      case 'comment':
        return <ChatBubbleLeftIcon className={iconClass} />;
      case 'reply':
        return <ArrowUturnLeftIcon className={iconClass} />;
      case 'mention':
        return <AtSymbolIcon className={iconClass} />;
      case 'follow':
        return <UserPlusIcon className={iconClass} />;
      case 'progress_milestone':
        return <FlagIcon className={iconClass} />;
      case 'aura_suggestion':
        return <SparklesIcon className={iconClass} />;
      default:
        return <BellIcon className={iconClass} />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal-overlay"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="surface-panel w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-hairline shrink-0">
              <div className="flex items-center gap-2">
                <BellIcon className="w-5 h-5 text-secondary" />
                <span className="font-display text-h2 text-primary">Notifications</span>
                {unreadCount > 0 && (
                  <span className="text-label text-pine px-2 py-0.5 border border-pine rounded-md bg-pine-tint">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-label text-pine hover:underline transition-colors duration-300"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="icon-btn"
                  aria-label="Close notifications"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="text-center text-secondary py-8 text-body">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="text-center text-secondary py-8">
                  <BellIcon className="w-10 h-10 text-faint mx-auto mb-2" />
                  <p className="text-body">No notifications yet</p>
                </div>
              ) : (
                <div className="hairline-list">
                  {notifications.map((notification) => (
                    <div
                      key={notification.notification_id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 cursor-pointer transition-colors duration-300 hover:bg-pine-tint/30 ${
                        !notification.read ? 'bg-pine-tint/20' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          {notification.actor_profile && (
                            <div className="flex items-center gap-2 mb-1">
                              <UserAvatar
                                userId={notification.actor_profile.user_id}
                                src={notification.actor_profile.avatar_url}
                                alt={notification.actor_profile.display_name}
                                size="xs"
                                rounded="lg"
                              />
                              <span className="text-primary font-medium text-sm">
                                {notification.actor_profile.display_name}
                              </span>
                            </div>
                          )}
                          <p className="text-secondary text-sm">{notification.message}</p>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-pine rounded-full mt-2" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationCenter;
